import React from 'react';
import { Box, SimpleGrid, FormControl, Tooltip, FormLabel, Input, HStack, Button, useToast, AlertDialog, AlertDialogBody, AlertDialogFooter, AlertDialogHeader, AlertDialogContent, AlertDialogOverlay, useDisclosure } from '@chakra-ui/react';
import { getTranslations } from '../translations';
import Cookies from 'js-cookie';
import RestTimeRules from './RestTimeRules';

// Helper for input styles
const InputControl = ({ label, value, setValue, tooltip, colorScheme, borderColor, isDarkMode }) => (
    <Box display="flex" flexDirection="column" alignItems="center" w="100%">
        <FormControl size="sm" position="relative" display="flex" flexDirection="column" alignItems="center" w="100%">
            <Box
                position="absolute"
                top="-10px" left="-10px" right="-10px" bottom="-10px"
                borderRadius="full"
                bgGradient={`linear(to-r, ${colorScheme}.200, ${colorScheme}.400)`}
                opacity="0.3"
                zIndex="0"
            />
            <Tooltip label={tooltip} hasArrow>
                <FormLabel fontSize="lg" color={isDarkMode ? "gray.200" : "gray.800"} fontWeight="extrabold" textAlign="center" mb={2}>
                    {label}
                </FormLabel>
            </Tooltip>
            <Input
                type="number"
                value={value}
                onChange={(e) => setValue(Math.min(9999, parseInt(e.target.value, 10)))}
                size="md"
                maxW="80px"
                maxLength={4}
                bg={isDarkMode ? "gray.700" : "white"}
                color={isDarkMode ? "white" : "gray.800"}
                borderWidth="4px"
                borderColor={borderColor}
                _hover={{ borderColor: `${colorScheme}.500`, transform: 'scale(1.05)' }}
                _focus={{ borderColor: `${colorScheme}.600`, boxShadow: `0 0 0 3px var(--chakra-colors-${colorScheme}-400)` }}
                transition="all 0.3s"
                borderRadius="xl"
                textAlign="center"
                fontSize="lg"
                position="relative"
                zIndex="1"
            />
        </FormControl>
    </Box>
);

const ConfigForm = ({
    workTime, setWorkTime,
    restTime, setRestTime,
    rounds, setRounds,
    startupTime, setStartupTime,
    isActive,
    savedConfigs, setSavedConfigs,
    setExercises,
    onDeleteConfig,
    isDarkMode,
    setCurrentProfileName,
    resetTimer,
    restTimeRules,
    setRestTimeRules
}) => {
    const t = getTranslations();
    const toast = useToast();

    // Alert Dialog State
    const { isOpen, onOpen, onClose } = useDisclosure();
    const cancelRef = React.useRef();
    const [configToDelete, setConfigToDelete] = React.useState(null);

    const loadConfig = (config) => {
        // Reset timer prima di caricare il nuovo profilo
        if (resetTimer) {
            resetTimer();
        }
        
        setWorkTime(config.workTime);
        setRestTime(config.restTime);
        setRounds(config.rounds);
        if (config.startupTime !== undefined) {
            setStartupTime(config.startupTime);
            // Salva anche nel cookie quando si carica un profilo
            Cookies.set('startupTime', config.startupTime.toString(), { expires: 365 });
        }
        if (config.restTimeRules !== undefined && setRestTimeRules) {
            setRestTimeRules(config.restTimeRules);
        } else if (setRestTimeRules) {
            setRestTimeRules([]);
        }

        // Imposta il nome del profilo corrente (può essere null o stringa)
        if (setCurrentProfileName) {
            setCurrentProfileName(config.name || null);
        }

        if (config.exercises && Array.isArray(config.exercises)) {
            // Restore exercises with new unique IDs
            const restoredExercises = config.exercises.map(item => {
                const isString = typeof item === 'string';
                return {
                    uniqueId: crypto.randomUUID(),
                    iconId: isString ? item : item.iconId,
                    customLabel: isString ? '' : (item.customLabel || '')
                };
            });
            setExercises(restoredExercises);
        } else {
            // Clear exercises if none are saved in this config
            // Calculate how many empty slots we need based on the config's rounds
            const emptyExercises = Array(config.rounds).fill(null).map(() => ({
                uniqueId: crypto.randomUUID(),
                iconId: null,
                customLabel: ''
            }));
            setExercises(emptyExercises);
        }

        toast({
            title: t.configLoaded,
            status: 'info',
            duration: 2000,
            isClosable: true,
        });
    };

    // Long Press Logic
    const handleButtonPress = (config, index) => {
        return {
            onMouseDown: () => handlePressStart(index),
            onMouseUp: () => handlePressEnd(config),
            onMouseLeave: () => handlePressCancel(),
            onTouchStart: () => handlePressStart(index),
            onTouchEnd: (e) => {
                e.preventDefault(); // Prevent ghost click
                handlePressEnd(config);
            }
        };
    };

    const timerRef = React.useRef(null);
    const isLongPress = React.useRef(false);

    const handlePressStart = (index) => {
        isLongPress.current = false;
        timerRef.current = setTimeout(() => {
            isLongPress.current = true;
            // Open confirmation dialog instead of deleting immediately
            setConfigToDelete(index);
            onOpen();
        }, 800); // 800ms for long press
    };

    const handlePressEnd = (config) => {
        if (timerRef.current) {
            clearTimeout(timerRef.current);
            timerRef.current = null;
        }
        if (!isLongPress.current) {
            loadConfig(config);
        }
    };

    const handlePressCancel = () => {
        if (timerRef.current) {
            clearTimeout(timerRef.current);
            timerRef.current = null;
        }
    };

    const handleConfirmDelete = () => {
        if (configToDelete !== null) {
            onDeleteConfig(configToDelete);
        }
        onClose();
        setConfigToDelete(null);
    };

    return (
        <Box w="100%">
            {!isActive && (
                <Box w="100%">
                    <HStack spacing={2} mb={4} w="100%" overflowX="auto" py={2} justifyContent="center">
                        {savedConfigs.map((config, index) => (
                            <Button
                                key={index}
                                size="sm"
                                colorScheme="teal"
                                {...handleButtonPress(config, index)}
                                minW="100px"
                                _active={{ transform: 'scale(0.95)' }}
                            >
                                {config.name || `${config.workTime}/${config.restTime}/${config.rounds}`}
                            </Button>
                        ))}
                    </HStack>
                </Box>
            )}

            {!isActive && (
                <SimpleGrid columns={2} spacing={8} w="100%" maxW="400px" mx="auto" alignItems="center" justifyContent="center">
                    <InputControl
                        label={t.workTime}
                        value={workTime}
                        setValue={setWorkTime}
                        tooltip={t.workTimeTooltip}
                        colorScheme="green"
                        borderColor="green.400"
                        isDarkMode={isDarkMode}
                    />
                    <InputControl
                        label={t.restTime}
                        value={restTime}
                        setValue={setRestTime}
                        tooltip={t.restTimeTooltip}
                        colorScheme="orange"
                        borderColor="orange.400"
                        isDarkMode={isDarkMode}
                    />
                    <Box gridColumn="span 2" display="flex" justifyContent="center" width="100%">
                        <Box width="50%">
                            <InputControl
                                label={t.rounds}
                                value={rounds}
                                setValue={setRounds}
                                tooltip={t.roundsTooltip}
                                colorScheme="yellow"
                                borderColor="yellow.400"
                                isDarkMode={isDarkMode}
                            />
                        </Box>
                    </Box>
                </SimpleGrid>
            )}

            {!isActive && (
                <RestTimeRules
                    rounds={rounds}
                    defaultRestTime={restTime}
                    restTimeRules={restTimeRules}
                    setRestTimeRules={setRestTimeRules}
                    isDarkMode={isDarkMode}
                    isActive={isActive}
                />
            )}

            <AlertDialog
                isOpen={isOpen}
                leastDestructiveRef={cancelRef}
                onClose={onClose}
            >
                <AlertDialogOverlay>
                    <AlertDialogContent bg={isDarkMode ? "gray.800" : "white"}>
                        <AlertDialogHeader fontSize="lg" fontWeight="bold" color={isDarkMode ? "white" : "gray.800"}>
                            Elimina Profilo
                        </AlertDialogHeader>

                        <AlertDialogBody color={isDarkMode ? "gray.300" : "gray.600"}>
                            Sei sicuro di voler eliminare questo profilo salvato?
                            L'azione non può essere annullata.
                        </AlertDialogBody>

                        <AlertDialogFooter>
                            <Button ref={cancelRef} onClick={onClose}>
                                Annulla
                            </Button>
                            <Button colorScheme="red" onClick={handleConfirmDelete} ml={3}>
                                Elimina
                            </Button>
                        </AlertDialogFooter>
                    </AlertDialogContent>
                </AlertDialogOverlay>
            </AlertDialog>
        </Box>
    );
};

export default ConfigForm;
